import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  LogIn, User, Lock, Eye, EyeOff, Mail,
  LayoutDashboard, BookOpen, ShoppingBag, Settings,
  TrendingUp, CheckCircle, Calendar, Bell, Star,
  Clock, Award, Package, ChevronRight, Phone,
  Truck, Zap, Target,
  ChevronDown, ExternalLink,
  X, Save, Building2, Lightbulb, DollarSign, Home,
  Download, Briefcase,
  Hammer, Building, Utensils, Store, Dumbbell, Cpu,
  Megaphone, GraduationCap, PartyPopper, Scale,
  Factory, Plane, TreePine, Music, PawPrint,
  Radio, UserCog, ShieldCheck, Trash2, Sun,
  FileSignature, Gamepad2, Palette, Camera, Printer,
  PenTool, Key, Warehouse, Leaf, MapPin, PackageOpen,
  Car, Scissors, Sparkles, Heart, Stethoscope
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import './YourHyyve.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Custom Pollen mascot image
const POLLEN_BEE_IMG = "https://customer-assets.emergentagent.com/job_bee-booking/artifacts/k8zgjyqb_Pollen%20-%20bHyyve%20Mascot.png";

// Business Categories (alphabetical order)
const BUSINESS_CATEGORIES = [
  { id: 'agriculture', name: 'Agriculture', icon: TreePine },
  { id: 'architecture-design', name: 'Architecture & Design', icon: Palette },
  { id: 'arts-entertainment', name: 'Arts & Entertainment', icon: Music },
  { id: 'automotive', name: 'Automotive', icon: Car },
  { id: 'beauty-personal-care', name: 'Beauty & Personal Care', icon: Scissors },
  { id: 'cleaning', name: 'Cleaning', icon: Sparkles },
  { id: 'consulting', name: 'Consulting', icon: Briefcase },
  { id: 'construction', name: 'Construction', icon: Hammer },
  { id: 'education-tutoring', name: 'Education & Tutoring', icon: GraduationCap },
  { id: 'event-planning', name: 'Event Planning', icon: PartyPopper },
  { id: 'financial-services', name: 'Financial Services', icon: DollarSign },
  { id: 'fitness', name: 'Fitness', icon: Dumbbell },
  { id: 'food-beverage', name: 'Food & Beverage', icon: Utensils },
  { id: 'gaming', name: 'Gaming', icon: Gamepad2 },
  { id: 'health-wellness', name: 'Health & Wellness', icon: Heart },
  { id: 'home-services', name: 'Home Services', icon: Home },
  { id: 'hospitality-tourism', name: 'Hospitality & Tourism', icon: Plane },
  { id: 'human-resources', name: 'Human Resources', icon: UserCog },
  { id: 'insurance', name: 'Insurance', icon: FileSignature },
  { id: 'landscaping', name: 'Landscaping', icon: Leaf },
  { id: 'legal-services', name: 'Legal Services', icon: Scale },
  { id: 'logistics-transportation', name: 'Logistics & Transportation', icon: Truck },
  { id: 'manufacturing', name: 'Manufacturing', icon: Factory },
  { id: 'marketing-advertising', name: 'Marketing & Advertising', icon: Megaphone },
  { id: 'medical-healthcare', name: 'Medical & Healthcare', icon: Stethoscope },
  { id: 'moving', name: 'Moving', icon: PackageOpen },
  { id: 'pet-services', name: 'Pet Services', icon: PawPrint },
  { id: 'photography-videography', name: 'Photography & Videography', icon: Camera },
  { id: 'property-management', name: 'Property Management', icon: MapPin },
  { id: 'publishing-printing', name: 'Publishing & Printing', icon: Printer },
  { id: 'real-estate', name: 'Real Estate', icon: Building },
  { id: 'renewable-energy', name: 'Renewable Energy', icon: Sun },
  { id: 'rental-services', name: 'Rental Services', icon: Key },
  { id: 'retail', name: 'Retail', icon: Store },
  { id: 'security-services', name: 'Security Services', icon: ShieldCheck },
  { id: 'software-development', name: 'Software Development', icon: Cpu },
  { id: 'technology', name: 'Technology', icon: Cpu },
  { id: 'telecommunications', name: 'Telecommunications', icon: Radio },
  { id: 'waste-management', name: 'Waste Management', icon: Trash2 },
  { id: 'wholesale-distribution', name: 'Wholesale & Distribution', icon: Warehouse },
  { id: 'writing-editing', name: 'Writing & Editing', icon: PenTool }
];

// Days of the week
const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

// Base Smart Tools (category-independent)
const BASE_SMART_TOOLS = [
  {
    id: 'business',
    title: 'Business Management',
    icon: LayoutDashboard,
    color: '#3b82f6',
    description: 'Manage your business profile, hours, and services',
    links: [
      { name: 'Business Profile', path: '/yourhyyve/profile', icon: User, modal: 'profile' },
      { name: 'Business Hours', path: '/yourhyyve/hours', icon: Clock, modal: 'hours' }
    ]
  },
  {
    id: 'orders',
    title: 'Orders & Bookings',
    icon: ShoppingBag,
    color: '#10b981',
    description: 'Track and manage customer orders and appointments',
    links: [
      { name: 'Download Vendor App', path: 'https://play.google.com/store/apps/details?id=com.partners.bhyyve', icon: Download, external: true }
    ]
  },
  {
    id: 'training',
    title: 'Training & Certifications',
    icon: BookOpen,
    color: '#8b5cf6',
    description: 'Access bHyyve University and earn certifications',
    links: [
      { name: 'My Courses', path: '/mycourses', icon: BookOpen, external: true },
      { name: 'Certifications', path: '/certifications', icon: Award, external: true },
      { name: 'Skill Assessments', path: '/assessments', icon: CheckCircle, external: true }
    ]
  },
  {
    id: 'marketplace',
    title: 'bHyyve Marketplace',
    icon: Package,
    color: '#f59e0b',
    description: 'Get supplies and equipment at exclusive discounts',
    links: [
      { name: 'Browse Products', path: '/marketplace', icon: ShoppingBag, external: true },
      { name: 'My Orders', path: '/myorders', icon: Truck, external: true }
    ]
  }
];

// Function to get smart tools based on category (placeholder for future customization)
const getSmartToolsForCategory = (category) => {
  // For now, return base tools - this will be customized per category in the future
  return BASE_SMART_TOOLS;
};

const YourHyyve = () => {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loginData, setLoginData] = useState({ phone: '', password: '' });
  const [vendorData, setVendorData] = useState(null);
  const [expandedTools, setExpandedTools] = useState([]);
  
  // Business Category selection state
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isSavingCategory, setIsSavingCategory] = useState(false);
  
  // Modal states
  const [activeModal, setActiveModal] = useState(null);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const fileInputRef = useRef(null);
  
  // Business Profile state - initialized empty, populated from vendor data
  const [businessProfile, setBusinessProfile] = useState({
    ownerName: '',
    businessName: '',
    partnerSince: '',
    phone: '',
    email: '',
    address: ''
  });
  
  // Business Hours state - default schedule
  const [businessHours, setBusinessHours] = useState({
    Monday: { open: '09:00', close: '17:00', closed: false },
    Tuesday: { open: '09:00', close: '17:00', closed: false },
    Wednesday: { open: '09:00', close: '17:00', closed: false },
    Thursday: { open: '09:00', close: '17:00', closed: false },
    Friday: { open: '09:00', close: '17:00', closed: false },
    Saturday: { open: '10:00', close: '14:00', closed: false },
    Sunday: { open: '00:00', close: '00:00', closed: true }
  });

  // Check for existing session on mount
  useEffect(() => {
    const savedSession = localStorage.getItem('vendorSession');
    if (savedSession) {
      try {
        const session = JSON.parse(savedSession);
        setVendorData(session.vendorData);
        setBusinessProfile(session.businessProfile || {
          ownerName: '',
          businessName: '',
          partnerSince: '',
          phone: '',
          email: '',
          address: ''
        });
        setIsLoggedIn(true);
        
        // Check if category needs to be selected
        if (!session.vendorData?.businessCategory) {
          setShowCategoryModal(true);
        }
      } catch (e) {
        localStorage.removeItem('vendorSession');
      }
    }
  }, []);

  // Save session to localStorage when vendor data changes
  useEffect(() => {
    if (isLoggedIn && vendorData) {
      localStorage.setItem('vendorSession', JSON.stringify({
        vendorData,
        businessProfile
      }));
    }
  }, [isLoggedIn, vendorData, businessProfile]);

  const handleLogout = () => {
    localStorage.removeItem('vendorSession');
    setIsLoggedIn(false);
    setVendorData(null);
    setExpandedTools([]);
    toast.success('Logged out successfully');
  };

  const toggleToolExpand = (toolId) => {
    setExpandedTools(prev => 
      prev.includes(toolId) 
        ? prev.filter(id => id !== toolId)
        : [...prev, toolId]
    );
  };

  // Handle tool link click
  const handleToolLinkClick = (e, link) => {
    e.preventDefault();
    if (link.external && link.path.startsWith('http')) {
      // External URL - open in new tab
      window.open(link.path, '_blank', 'noopener,noreferrer');
    } else if (link.external) {
      // Internal route with external flag - navigate within app
      navigate(link.path);
    } else if (link.modal) {
      setActiveModal(link.modal);
    } else {
      toast.info(`${link.name} - Coming Soon!`);
    }
  };

  // Handle business hours change
  const handleHoursChange = (day, field, value) => {
    setBusinessHours(prev => ({
      ...prev,
      [day]: { ...prev[day], [field]: value }
    }));
  };

  // Save handlers
  const saveBusinessProfile = async () => {
    setIsSavingProfile(true);
    try {
      const response = await fetch(`${API_URL}/api/vendor-portal/update-profile`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: vendorData.id,
          businessName: businessProfile.businessName,
          ownerName: businessProfile.ownerName,
          phone: businessProfile.phone,
          email: businessProfile.email,
          address: businessProfile.address
        })
      });

      if (response.ok) {
        // Update vendorData with new name
        const nameParts = businessProfile.ownerName.split(' ', 1);
        setVendorData(prev => ({
          ...prev,
          name: businessProfile.ownerName,
          firstName: nameParts[0],
          lastName: nameParts[1] || '',
          businessName: businessProfile.businessName,
          email: businessProfile.email,
          phone: businessProfile.phone
        }));
        toast.success('Business profile saved successfully!');
        setActiveModal(null);
      } else {
        toast.error('Failed to save profile. Please try again.');
      }
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error('Unable to save. Please try again.');
    } finally {
      setIsSavingProfile(false);
    }
  };

  const saveBusinessHours = () => {
    toast.success('Business hours updated successfully!');
    setActiveModal(null);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/vendor-portal/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginData)
      });

      // Handle based on status code first
      if (response.status === 401) {
        toast.error('Invalid phone number or password');
        setIsLoading(false);
        return;
      }
      
      if (response.status === 403) {
        toast.error('Your application is still under review. We\'ll email you once approved!');
        setIsLoading(false);
        return;
      }
      
      if (!response.ok) {
        toast.error('Login failed. Please try again.');
        setIsLoading(false);
        return;
      }

      // Only parse JSON for successful responses
      const data = await response.json();
      
      const userData = {
        id: data.user.id,
        name: `${data.user.firstName} ${data.user.lastName}`,
        firstName: data.user.firstName,
        lastName: data.user.lastName,
        email: data.user.email,
        phone: data.user.phone,
        businessName: data.user.businessName,
        businessCategory: data.user.businessCategory,
        status: 'active'
      };
      
      setVendorData(userData);
      
      // Update business profile with user data
      setBusinessProfile({
        ownerName: `${data.user.firstName} ${data.user.lastName}`,
        businessName: data.user.businessName || '',
        partnerSince: new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
        phone: data.user.phone || '',
        email: data.user.email || '',
        address: ''
      });
      
      setIsLoggedIn(true);
      
      // Check if user needs to select business category
      if (!data.user.businessCategory) {
        setShowCategoryModal(true);
        toast.success('Welcome! Please select your business category to get started.');
      } else {
        toast.success('Welcome back to your Hyyve!');
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error('Unable to connect. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle category selection
  const handleCategorySelect = async () => {
    if (!selectedCategory) {
      toast.error('Please select a business category');
      return;
    }
    
    setIsSavingCategory(true);
    
    try {
      const response = await fetch(`${API_URL}/api/vendor-portal/update-category`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: vendorData.id,
          businessCategory: selectedCategory
        })
      });
      
      if (response.ok) {
        setVendorData(prev => ({ ...prev, businessCategory: selectedCategory }));
        setShowCategoryModal(false);
        toast.success('Business category saved! Your dashboard is now personalized.');
      } else {
        toast.error('Failed to save category. Please try again.');
      }
    } catch (error) {
      console.error('Error saving category:', error);
      toast.error('Unable to save. Please try again.');
    } finally {
      setIsSavingCategory(false);
    }
  };

  // Render Business Profile Modal
  const renderProfileModal = () => (
    <div className="modal-overlay" onClick={() => setActiveModal(null)}>
      <div className="modal-content profile-modal" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={() => setActiveModal(null)}>
          <X size={24} />
        </button>
        
        <div className="modal-header">
          <div className="modal-icon" style={{ background: '#3b82f6' }}>
            <Building2 size={28} />
          </div>
          <div>
            <h2>Business Profile</h2>
            <p>Manage your business information</p>
          </div>
        </div>

        <div className="profile-badge">
          <img src={POLLEN_BEE_IMG} alt="Pollen" className="profile-bee" />
          <div className="badge-info">
            <span className="badge-label">bHyyve Partner Since</span>
            <span className="badge-date">{businessProfile.partnerSince}</span>
          </div>
        </div>

        <div className="modal-form">
          <div className="form-group">
            <label><User size={16} /> Owner Name</label>
            <Input 
              value={businessProfile.ownerName}
              onChange={e => setBusinessProfile({...businessProfile, ownerName: e.target.value})}
              placeholder="Your full name"
            />
          </div>
          
          <div className="form-group">
            <label><Building2 size={16} /> Business Name</label>
            <Input 
              value={businessProfile.businessName}
              onChange={e => setBusinessProfile({...businessProfile, businessName: e.target.value})}
              placeholder="Your business name"
            />
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label><Phone size={16} /> Phone</label>
              <Input 
                value={businessProfile.phone}
                onChange={e => setBusinessProfile({...businessProfile, phone: e.target.value})}
                placeholder="Phone number"
              />
            </div>
            <div className="form-group">
              <label><Mail size={16} /> Email</label>
              <Input 
                value={businessProfile.email}
                onChange={e => setBusinessProfile({...businessProfile, email: e.target.value})}
                placeholder="Email address"
              />
            </div>
          </div>
          
          <div className="form-group">
            <label><Target size={16} /> Business Address</label>
            <Input 
              value={businessProfile.address}
              onChange={e => setBusinessProfile({...businessProfile, address: e.target.value})}
              placeholder="Your business address"
            />
          </div>
        </div>

        <div className="modal-actions">
          <Button className="cancel-btn" onClick={() => setActiveModal(null)}>Cancel</Button>
          <Button className="save-btn" onClick={saveBusinessProfile} disabled={isSavingProfile}>
            {isSavingProfile ? 'Saving...' : <><Save size={18} /> Save Changes</>}
          </Button>
        </div>
      </div>
    </div>
  );

  // Render Business Hours Modal
  const renderHoursModal = () => (
    <div className="modal-overlay" onClick={() => setActiveModal(null)}>
      <div className="modal-content hours-modal" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={() => setActiveModal(null)}>
          <X size={24} />
        </button>
        
        <div className="modal-header">
          <div className="modal-icon" style={{ background: '#10b981' }}>
            <Clock size={28} />
          </div>
          <div>
            <h2>Business Hours</h2>
            <p>Set your operating hours for each day</p>
          </div>
        </div>

        <div className="hours-list">
          {DAYS_OF_WEEK.map(day => (
            <div key={day} className={`hours-row ${businessHours[day].closed ? 'closed' : ''}`}>
              <div className="day-name">{day}</div>
              <div className="hours-inputs">
                {businessHours[day].closed ? (
                  <span className="closed-label">Closed</span>
                ) : (
                  <>
                    <Input 
                      type="time" 
                      value={businessHours[day].open}
                      onChange={e => handleHoursChange(day, 'open', e.target.value)}
                      className="time-input"
                    />
                    <span className="time-separator">to</span>
                    <Input 
                      type="time" 
                      value={businessHours[day].close}
                      onChange={e => handleHoursChange(day, 'close', e.target.value)}
                      className="time-input"
                    />
                  </>
                )}
              </div>
              <label className="closed-toggle">
                <input 
                  type="checkbox" 
                  checked={businessHours[day].closed}
                  onChange={e => handleHoursChange(day, 'closed', e.target.checked)}
                />
                <span>Closed</span>
              </label>
            </div>
          ))}
        </div>

        <div className="hours-tips">
          <Lightbulb size={18} />
          <span>Tip: Keep consistent hours to build customer trust. Customers can see your availability when booking.</span>
        </div>

        <div className="modal-actions">
          <Button className="cancel-btn" onClick={() => setActiveModal(null)}>Cancel</Button>
          <Button className="save-btn" onClick={saveBusinessHours}>
            <Save size={18} /> Save Hours
          </Button>
        </div>
      </div>
    </div>
  );

  // Render Business Category Selection Modal
  const renderCategoryModal = () => (
    <div className="modal-overlay category-modal-overlay">
      <div className="modal-content category-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-icon" style={{ background: '#f59e0b' }}>
            <Briefcase size={28} />
          </div>
          <div>
            <h2>Select Your Business Category</h2>
            <p>This helps us personalize your dashboard and tools</p>
          </div>
        </div>

        <div className="category-grid">
          {BUSINESS_CATEGORIES.map((category) => {
            const CategoryIcon = category.icon;
            const isSelected = selectedCategory === category.id;
            return (
              <button
                key={category.id}
                className={`category-card ${isSelected ? 'selected' : ''}`}
                onClick={() => setSelectedCategory(category.id)}
              >
                <div className="category-icon">
                  <CategoryIcon size={24} />
                </div>
                <h3>{category.name}</h3>
                {isSelected && (
                  <div className="selected-check">
                    <CheckCircle size={18} />
                  </div>
                )}
              </button>
            );
          })}
        </div>

        <div className="modal-actions">
          <Button 
            className="save-btn category-save-btn" 
            onClick={handleCategorySelect}
            disabled={!selectedCategory || isSavingCategory}
          >
            {isSavingCategory ? (
              <>Saving...</>
            ) : (
              <><CheckCircle size={18} /> Continue to Dashboard</>
            )}
          </Button>
        </div>
      </div>
    </div>
  );

  // Get smart tools based on vendor's category
  const smartTools = getSmartToolsForCategory(vendorData?.businessCategory);

  // Landing Page (Not Logged In)
  if (!isLoggedIn) {
    return (
      <div className="yourhyyve-page">
        {/* Header */}
        <header className="hyyve-header">
          <div className="header-content">
            <a href="/" className="hyyve-logo">
              <div className="logo-icon">H</div>
              <span>Your <span className="highlight">Hyyve</span></span>
            </a>
            <nav className="header-nav">
              <a href="tel:+19054958888" className="nav-phone">
                <Phone size={16} />
                +1 (905) 495-8888
              </a>
              <Button className="login-btn-header" onClick={() => document.getElementById('login-section').scrollIntoView({ behavior: 'smooth' })}>
                <LogIn size={16} />
                Log In
              </Button>
            </nav>
          </div>
        </header>

        {/* Hero Section */}
        <section className="hyyve-hero">
          {/* Hero Content */}
          <div className="hero-content">
            <h1 className="hero-title">
              Manage your business<br/>
              <span className="highlight">in one app</span>
            </h1>
            
            <p className="hero-subtitle">
              Your Hyyve gives you all the tools to manage your business, track orders, 
              access training, and grow your customer base—wherever business happens
            </p>

            {/* 4 Steps */}
            <div className="steps-container">
              <p className="steps-title">4 Simple Steps to Get Started</p>
              <div className="steps-grid">
                <div className="step-item">
                  <div className="step-number">1</div>
                  <User size={20} />
                  <span>Create Profile</span>
                </div>
                <div className="step-item">
                  <div className="step-number">2</div>
                  <BookOpen size={20} />
                  <span>Complete Training</span>
                </div>
                <div className="step-item">
                  <div className="step-number">3</div>
                  <Package size={20} />
                  <span>Get Supplies</span>
                </div>
                <div className="step-item">
                  <div className="step-number">4</div>
                  <TrendingUp size={20} />
                  <span>Start Earning</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="features-section">
          <h2 className="section-title">All smart tools in one place</h2>
          
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon"><LayoutDashboard size={24} /></div>
              <h3>Business Dashboard</h3>
              <p>Real-time overview of orders, earnings, and customer feedback</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon"><BookOpen size={24} /></div>
              <h3>Training & Certifications</h3>
              <p>Access bHyyve University courses and earn professional certifications</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon"><ShoppingBag size={24} /></div>
              <h3>bHyyve Marketplace</h3>
              <p>Get supplies and equipment at exclusive vendor discounts</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon"><Bell size={24} /></div>
              <h3>Order Management</h3>
              <p>Receive and manage customer orders in real-time</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon"><Star size={24} /></div>
              <h3>Reviews & Ratings</h3>
              <p>Build your reputation with customer reviews and ratings</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon"><Settings size={24} /></div>
              <h3>Business Settings</h3>
              <p>Manage your profile, hours, services, and pricing</p>
            </div>
          </div>
        </section>

        {/* Login Section */}
        <section className="login-section" id="login-section">
          <div className="login-container">
            <div className="login-left">
              <img src={POLLEN_BEE_IMG} alt="Pollen the bee" className="login-bee" />
              <h2>Welcome to Your Hyyve</h2>
              <p>Log in to access your vendor dashboard, manage orders, track earnings, and grow your business.</p>
              
              <div className="login-benefits">
                <div className="benefit-item">
                  <CheckCircle size={20} />
                  <span>Track your earnings & payouts</span>
                </div>
                <div className="benefit-item">
                  <CheckCircle size={20} />
                  <span>Access exclusive training</span>
                </div>
                <div className="benefit-item">
                  <CheckCircle size={20} />
                  <span>Manage your business profile</span>
                </div>
              </div>
            </div>
            
            <div className="login-right">
              <div className="login-card">
                <h3>Vendor Login</h3>
                <p>Enter your credentials to access your Hyyve</p>
                
                <form onSubmit={handleLogin} className="login-form">
                  <div className="input-group">
                    <Phone size={20} className="input-icon" />
                    <Input
                      type="tel"
                      placeholder="Phone number"
                      value={loginData.phone}
                      onChange={(e) => setLoginData({...loginData, phone: e.target.value})}
                      required
                      data-testid="login-phone"
                    />
                  </div>
                  
                  <div className="input-group">
                    <Lock size={20} className="input-icon" />
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Password"
                      value={loginData.password}
                      onChange={(e) => setLoginData({...loginData, password: e.target.value})}
                      required
                      data-testid="login-password"
                    />
                    <button 
                      type="button" 
                      className="password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                  
                  <div className="login-options">
                    <label className="remember-me">
                      <input type="checkbox" />
                      <span>Remember me</span>
                    </label>
                    <a href="#forgot" className="forgot-link">Forgot password?</a>
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="login-submit-btn"
                    disabled={isLoading}
                    data-testid="login-submit"
                  >
                    {isLoading ? 'Signing in...' : 'Sign In'}
                    <ChevronRight size={20} />
                  </Button>
                </form>
                
                <div className="login-footer">
                  <p>Don&apos;t have an account? <a href="/vendoronboarding">Apply to become a vendor</a></p>
                  <p className="contact-info">Need help? Call us at <a href="tel:905-495-8888">905-495-8888</a></p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="hyyve-footer">
          <p>© 2026 bHyyve Technologies. All rights reserved.</p>
        </footer>
      </div>
    );
  }

  // Dashboard (Logged In)
  return (
    <div className="yourhyyve-dashboard">
      {/* Dashboard Header */}
      <header className="dashboard-header">
        <div className="header-content">
          <a href="/yourhyyve" className="hyyve-logo">
            <div className="logo-icon">H</div>
            <span>Your <span className="highlight">Hyyve</span></span>
          </a>
          <div className="user-menu">
            <Bell size={20} className="notification-icon" />
            <div className="user-avatar">
              {vendorData?.name?.charAt(0) || 'V'}
            </div>
            <span className="user-name">{vendorData?.name || 'Vendor'}</span>
          </div>
        </div>
      </header>

      {/* Dashboard Content */}
      <div className="dashboard-layout">
        {/* Sidebar */}
        <aside className="dashboard-sidebar">
          <nav className="sidebar-nav">
            <a href="#dashboard" className="nav-item active">
              <LayoutDashboard size={20} />
              <span>Dashboard</span>
            </a>
            <a href="#tools" className="nav-item">
              <Zap size={20} />
              <span>Smart Tools</span>
            </a>
          </nav>
          
          <button 
            className="logout-btn"
            onClick={handleLogout}
          >
            <LogIn size={20} />
            <span>Log Out</span>
          </button>
        </aside>

        {/* Main Content */}
        <main className="dashboard-main">
          {/* Welcome Banner */}
          <div className="welcome-banner">
            <div className="welcome-content">
              <h1>Welcome back, {vendorData?.name?.split(' ')[0] || 'Vendor'}! 🐝</h1>
              <p>Here&apos;s what&apos;s happening with your business today</p>
            </div>
            <img src={POLLEN_BEE_IMG} alt="Pollen" className="welcome-bee" />
          </div>

          {/* Smart Tools Section */}
          <section className="smart-tools-section" id="tools">
            <h2 className="section-heading">
              <Zap size={24} />
              Smart Tools
            </h2>
            <p className="section-subheading">Click on any tool to expand and access its features</p>

            <div className="tools-grid">
              {smartTools.map((tool) => {
                const isExpanded = expandedTools.includes(tool.id);
                const ToolIcon = tool.icon;
                
                return (
                  <div 
                    key={tool.id} 
                    className={`tool-card ${isExpanded ? 'expanded' : ''}`}
                    style={{ '--tool-color': tool.color }}
                  >
                    <button 
                      className="tool-header"
                      onClick={() => toggleToolExpand(tool.id)}
                    >
                      <div className="tool-icon-wrapper" style={{ background: tool.color }}>
                        <ToolIcon size={24} />
                      </div>
                      <div className="tool-info">
                        <h3>{tool.title}</h3>
                        <p>{tool.description}</p>
                      </div>
                      <ChevronDown 
                        size={24} 
                        className={`expand-icon ${isExpanded ? 'rotated' : ''}`}
                      />
                    </button>
                    
                    {isExpanded && (
                      <div className="tool-links">
                        {tool.links.map((link, index) => {
                          const LinkIcon = link.icon;
                          return (
                            <a 
                              key={index}
                              href={link.path}
                              className="tool-link"
                              onClick={(e) => handleToolLinkClick(e, link)}
                            >
                              <LinkIcon size={18} />
                              <span>{link.name}</span>
                              <ExternalLink size={14} className="link-arrow" />
                            </a>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        </main>
      </div>

      {/* Modals */}
      {activeModal === 'profile' && renderProfileModal()}
      {activeModal === 'hours' && renderHoursModal()}
      {showCategoryModal && renderCategoryModal()}
    </div>
  );
};

export default YourHyyve;
